import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Box, Heading, Modal, Page, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import { ErrorMessageConstants, useSubmitSupportRequest } from '@zg-rentals/rental-manager-env';

import { useGetHelpArticles, useGetHelpTopics } from '../../hooks/queries/support';

import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';

import { useAuth } from '../../components/AuthProvider';
import HelpArticlesContent from '../../components/SubmitRequest/HelpArticlesContent';
import SubmitRequestForm from '../../components/SubmitRequest/SubmitRequestForm';
import SubmitRequestPageSkeleton from '../../components/SubmitRequest/SubmitRequestPageSkeleton';

import type {
  GuestSupportGetHelpTopicsCategoryTopicWebDto,
  GuestSupportGetHelpTopicsCategoryWebDto,
  GuestSupportSubmitRequestWebDto,
  RentalManagerSupportHelpCategory,
  RentalManagerSupportHelpTopic,
} from '@zg-rentals/ts-rental-manager-api-client';

const emptyCategory = (): GuestSupportGetHelpTopicsCategoryWebDto => ({
  key: '' as RentalManagerSupportHelpCategory,
  description: '',
  topics: [],
});

const emptyTopic = (): GuestSupportGetHelpTopicsCategoryTopicWebDto => ({
  key: '' as RentalManagerSupportHelpTopic,
  description: '',
  descriptionFieldPlaceholder: '',
});

const SubmitRequestPage = () => {
  const router = useRouter();
  const { user } = useAuth();

  const isLoggedIn = user?.loggedIn ?? false;

  const [submitRequestError, setSubmitRequestError] = useState('');
  const [manualSelection, setManualSelection] = useState<{
    category: GuestSupportGetHelpTopicsCategoryWebDto;
    topic: GuestSupportGetHelpTopicsCategoryTopicWebDto;
  } | null>(null);

  const { data: helpTopics, isLoading: isHelpTopicsLoading, error: helpTopicsError } = useGetHelpTopics({ isLoggedIn });

  const {
    submit: submitSupportRequest,
    isPending: isSubmitting,
    isSuccess: isSubmitSuccess,
    reset: resetSubmitRequest,
  } = useSubmitSupportRequest(isLoggedIn);

  const queryCategoryKey = router.isReady && typeof router.query.category === 'string' ? router.query.category : '';
  const queryTopicKey = router.isReady && typeof router.query.topic === 'string' ? router.query.topic : '';
  const queryListingAddress =
    router.isReady && typeof router.query.listingAddress === 'string' ? router.query.listingAddress : '';
  const queryUnit = router.isReady && typeof router.query.unit === 'string' ? router.query.unit : '';
  const queryListingAlias =
    router.isReady && typeof router.query.listingAlias === 'string' ? router.query.listingAlias : '';

  const queryBinding = useMemo(() => {
    if (!router.isReady || !helpTopics?.categories?.length || !queryCategoryKey) {
      return null;
    }
    const category = helpTopics.categories.find((c) => (c.key as string) === queryCategoryKey);
    if (!category) {
      return null;
    }
    const matchedTopic =
      queryTopicKey !== '' ? category.topics?.find((t) => (t.key as string) === queryTopicKey) : undefined;
    return { category, matchedTopic };
  }, [router.isReady, helpTopics, queryCategoryKey, queryTopicKey]);

  const selectedHelpCategory =
    manualSelection !== null ? manualSelection.category : (queryBinding?.category ?? emptyCategory());
  const selectedHelpTopic =
    manualSelection !== null ? manualSelection.topic : (queryBinding?.matchedTopic ?? emptyTopic());

  const isCategoryReadOnly =
    Boolean(queryBinding) && !(manualSelection !== null && (manualSelection.category.key as string) === '');
  const isTopicReadOnly = Boolean(queryTopicKey && queryBinding?.matchedTopic) && manualSelection === null;

  //  blank category will retrieve FAQ for the initial page state
  const { error: helpArticlesError } = useGetHelpArticles({
    category: selectedHelpCategory?.key || '',
    topic: selectedHelpTopic?.key || '',
    isLoggedIn,
  });

  const handleCategorySelect = (category: GuestSupportGetHelpTopicsCategoryWebDto) => {
    setManualSelection({ category, topic: emptyTopic() });
  };

  const handleTopicSelect = (topic: GuestSupportGetHelpTopicsCategoryTopicWebDto) => {
    setManualSelection((prev) => ({
      category: prev?.category ?? queryBinding?.category ?? emptyCategory(),
      topic,
    }));
  };

  const handleSubmitRequest = (params: GuestSupportSubmitRequestWebDto) => {
    submitSupportRequest(
      {
        categoryKey: params.category ?? '',
        topicKey: params.topic ?? '',
        topicDescription: selectedHelpTopic?.description ?? '',
        description: params.description,
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
        listingAddress: params.listingAddress ?? '',
        listingAlias: params.listingAlias,
        unit: params.unit,
      },
      {
        onError: (message) => setSubmitRequestError(message),
      },
    );
  };

  const resetForm = () => {
    setManualSelection({ category: emptyCategory(), topic: emptyTopic() });
    resetSubmitRequest();
    setSubmitRequestError('');
  };

  const signInUrl = `${EXTERNAL_ROUTES.SIGN_IN}?url=/rental-manager${ROUTES.SUBMIT_REQUEST}`;

  const renderSuccess = () => (
    <Alert tone="success" css={{ margin: '600' }}>
      <Text>
        Thank you! Your request has been submitted. <TextButton onClick={resetForm}>Submit another request.</TextButton>
      </Text>
    </Alert>
  );

  const renderForm = () => (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
      <Box css={{ gridColumnEnd: { base: 'span 12', xl: 'span 8' } }}>
        <SubmitRequestForm
          onSubmit={handleSubmitRequest}
          queryListingAddress={queryListingAddress}
          queryUnit={queryUnit}
          queryListingAlias={queryListingAlias}
          isCategoryReadOnly={isCategoryReadOnly}
          isTopicReadOnly={isTopicReadOnly}
          isSubmitting={isSubmitting}
          selectedHelpCategory={selectedHelpCategory}
          selectedHelpTopic={selectedHelpTopic}
          isLoggedIn={isLoggedIn}
          userFirstName={user?.firstName}
          userLastName={user?.lastName}
          userEmail={user?.email}
          signInUrl={signInUrl}
          onCategorySelect={handleCategorySelect}
          onTopicSelect={handleTopicSelect}
        />
      </Box>
      <Box css={{ gridColumnEnd: { base: 'span 12', xl: 'span 4' } }}>
        <HelpArticlesContent
          selectedHelpCategory={selectedHelpCategory}
          selectedHelpTopic={selectedHelpTopic}
          isLoggedIn={isLoggedIn}
        />
      </Box>
      {submitRequestError && (
        <Modal
          open={true}
          onOpenChange={(open) => !open && setSubmitRequestError('')}
          header={<Heading level={3}>Unable to submit request</Heading>}
          body={<Text>{submitRequestError}</Text>}
        />
      )}
    </Box>
  );

  const renderState = () => {
    switch (true) {
      case Boolean(helpArticlesError || helpTopicsError):
        return <Alert tone="critical">{ErrorMessageConstants.DEFAULT_ERROR}</Alert>;
      case Boolean(isSubmitSuccess):
        return renderSuccess();
      case Boolean(isHelpTopicsLoading):
        return <SubmitRequestPageSkeleton />;
      case Boolean(helpTopics?.categories?.length):
        return renderForm();
      default:
        return null;
    }
  };

  return (
    <Page.Root data-testid="SubmitRequestPage" css={{ backgroundColor: 'bg.neutral', xl: { minHeight: '900px' } }}>
      <Page.Breadcrumb>
        <TextButton asChild icon={<IconChevronLeftFilled />}>
          <a href="https://www.zillow.help/renter-landlord-property-manager">Help center | Zillow Rental Manager</a>
        </TextButton>
      </Page.Breadcrumb>
      <Page.Header>
        <Heading level={1} fontFamily="heading-alt" textStyle="heading-md">
          Submit a request
        </Heading>
      </Page.Header>
      <Page.Content>{renderState()}</Page.Content>
    </Page.Root>
  );
};

export default SubmitRequestPage;
